import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterBoxProps } from './index';
import { IPriceRange } from '../../../redux/filters/filter.duck.interface';
import { FilterDuck } from '../../../redux/filters/filter.duck';
import { filtersToUrl, modifyFilters } from '../../../services/filters/utils/Filters.utils';
import { stringify } from 'query-string';
import { FINANCE } from '../../../services/filters/filters';
import Icon, { Icons } from '../../Icon';
import { buildParameterizedPathname } from '@utils/url.utils';
import TrimSelectorDuck from '../../../redux/trimSelector/trimSelector.duck';
import GeneratedFilterButtons from './Components/GeneratedFilterButtons';
import PriceLegalText from '../../PriceLegalText';
import { useTranslations } from '@hooks/useTranslations';
import { useCustomRouter } from '@hooks/useCustomRouter';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import { useGTM } from '@hooks/useGTM';
import OfferListDuck from '../../../redux/carList/offerlist.duck';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import GlobalDuck from '../../../redux/global/global.duck';
import GeneratedExtraFilters from './Components/GeneratedExtraFilters';
import { Redux } from '../../../redux';
import { getHasMonthlyPrice } from './utils';
import { theme } from '../../../styles/theme';
import FinanceToggle from './Components/FinanceToggle';
import { FILTER_CATEGORY } from '../../../constants/main';
import { SortOrder, SortType } from '../../../services/stock/models/stockItems.service.model';

export enum GTMActionValues {
    APPLY = 'Apply',
    REMOVE = 'Remove',
    FILTER = 'Filter',
    PRICE_VIEW = 'PriceView',
    PRICE = 'Price',
    MAX = 'max',
    MIN = 'min',
}

const RegisteredFiltersForUnifiedGTMUpdate = [
    FILTER_CATEGORY.ENERGIES,
    FILTER_CATEGORY.BODY_STYLES,
    FILTER_CATEGORY.COLOR_GROUPS,
    FILTER_CATEGORY.GEARBOX_TYPES,
    FILTER_CATEGORY.TRANSMISSION_TYPES,
];

export const capitalizeFirstLetter = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const extraFieldParentNames: string[] = [FILTER_CATEGORY.COLOR_GROUPS, FILTER_CATEGORY.ENGINES, FILTER_CATEGORY.GRADES];

const FilterBoxTemplate: FC<FilterBoxProps> = ({ className, isHomepage, onGeoLocationChange }) => {
    const { t } = useTranslations();
    const { pushUserAction, pushGTMAction } = useGTM();
    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();
    const router = useCustomRouter();
    const dispatch = useDispatch();

    const allTrims = useSelector((state: Redux) => TrimSelectorDuck.getOwnState(state).stock.stockTrims);
    const filters = useSelector((state: Redux) => FilterDuck.getFilters(state));
    const paymentJourney = useSelector((state: Redux) => FilterDuck.getPaymentJourneyType(state));
    const availableFilters = useSelector((state: Redux) => FilterDuck.getFilteredFilters(state));
    const sort = useSelector((state: Redux) => FilterDuck.getSorting(state));
    const filterCategories = useSelector((state: Redux) => FilterDuck.getFilterCategories(state));
    const priceRange = useSelector((state: Redux) => FilterDuck.getPriceRange(state));
    const allOffers = useSelector((state: Redux) => OfferListDuck.getStockOffers(state));
    const isRouting = useSelector((state: Redux) => UIDuck.getIsRouting(state));
    const carJourney = useSelector((state: Redux) => GlobalDuck.getCarJourney(state));
    const geoLocation = useSelector((state: Redux) => FilterDuck.getGeoLocation(state));
    const distanceRadius = useSelector((state: Redux) => FilterDuck.getDistanceRadius(state));
    const getGeoLocationName = useSelector((state: Redux) => FilterDuck.getGeoLocationName(state));

    const query = typeof window !== 'undefined' ? location.search : stringify(router.query);
    const [mobileHide, setMobileHide] = useState(true);
    const [oldPrices, setOldPrices] = useState(priceRange);
    const [areExtraFiltersCollapsed, setAreExtraFiltersCollapsed] = useState(true);

    const monthlyPrice = isHomepage ? getHasMonthlyPrice(allOffers) : getHasMonthlyPrice(allTrims);
    const hasMonthlyPrices = paymentJourney === FINANCE || !!monthlyPrice;

    const queryString = filtersToUrl(
        filterCategories,
        filters,
        sort,
        query,
        geoLocation,
        distanceRadius,
        getGeoLocationName,
        priceRange
    );
    const formattedPathname = buildParameterizedPathname(router.asPath.replace(/\?(.*)/, ''), carJourney);

    const baseFilters = useMemo(
        () => filters.filter(({ parent }) => !extraFieldParentNames.includes(parent)),
        [filters]
    );

    const extraFilters = useMemo(
        () => filters.filter(({ parent }) => extraFieldParentNames.includes(parent)),
        [filters]
    );

    const selectedFiltersCount = useMemo(() => filters.filter((filter) => !!filter.value).length, [filters]);

    useEffect(() => {
        if (filters) {
            const doQueriesMatch = window?.location?.search === queryString;
            const doPathnamesMatch = router.asPath.replace(/\?(.*)/, '') === formattedPathname;

            if ((!doQueriesMatch || !doPathnamesMatch) && !isRouting && carJourney) {
                router.replace(`${formattedPathname}${queryString}`, { scroll: false });
            }
        }
    }, [queryString, formattedPathname]);

    const changeBudgetType = useCallback(
        (option: string) => {
            const priceRangeFilter = filters.find((filter) => filter.name === FILTER_CATEGORY.PRICES);

            if (priceRangeFilter) {
                const newFilters = modifyFilters(filters, priceRangeFilter.name, null);
                dispatch(FilterDuck.setFilters(newFilters));
            }
            dispatch(FilterDuck.changeBudgetType(option));

            pushUserAction({
                action: `${GTMActionValues.FILTER}::${GTMActionValues.PRICE_VIEW}`,
                label: option,
            });
        },
        [filters]
    );

    const handleChange = (name: string, value: boolean | IPriceRange) => {
        const newFilters = modifyFilters(filters, name, value);

        dispatch(FilterDuck.setFilters(newFilters));

        const gtmRegisteredFilter = RegisteredFiltersForUnifiedGTMUpdate.find((gtmFilter) => name.includes(gtmFilter));

        pushGTMAction(name, value, oldPrices, { fireSPAEvent: true });

        if (gtmRegisteredFilter) {
            const gtmAction = value ? GTMActionValues.APPLY : GTMActionValues.REMOVE;

            pushUserAction({
                action: `${GTMActionValues.FILTER}::${capitalizeFirstLetter(gtmRegisteredFilter)}`,
                label: `${gtmAction}::${name.replace(`${gtmRegisteredFilter}.`, '')}`,
            });
        }

        if (name === FILTER_CATEGORY.PRICES) {
            const { min: oldMin, max: oldMax } = oldPrices;
            const { min: newMin, max: newMax } = value as IPriceRange;

            if (oldMin !== newMin) {
                pushUserAction({
                    action: `${GTMActionValues.FILTER}::${GTMActionValues.PRICE}`,
                    label: `${GTMActionValues.MIN}`,
                    value: String(newMin),
                });
            }

            if (oldMax !== newMax) {
                pushUserAction({
                    action: `${GTMActionValues.FILTER}::${GTMActionValues.PRICE}`,
                    label: `${GTMActionValues.MAX}`,
                    value: String(newMax),
                });
            }

            setOldPrices(value as IPriceRange);
        }
    };

    const handleCollapseExtraFiltersClick = () => {
        pushUserAction({
            action: `${GTMActionValues.FILTER}`,
            label: 'Show More',
        });

        setAreExtraFiltersCollapsed((flag) => !flag);
    };

    const resetFilters = () => {
        dispatch(FilterDuck.resetFilters(filters));

        const formattedPathname = buildParameterizedPathname(
            `${window.location.pathname}?sort${SortType.PRICE}:${SortOrder.ASC}`,
            carJourney
        );
        router.replace(formattedPathname, { scroll: false });
    };

    const renderResetButton = useCallback(
        (className: string) => (
            <button
                className={`reset-filters--button ${className}`}
                onClick={resetFilters}
                data-testid="TESTING_MOBILE_RESET_BTN"
            >
                {t('filters.reset')}
            </button>
        ),
        [mobileHide, resetFilters]
    );

    const renderFinanceToggle = useMemo(
        () => (
            <FinanceToggle
                budget={paymentJourney}
                hasMonthlyPrices={hasMonthlyPrices}
                changeBudgetType={changeBudgetType}
            />
        ),
        [paymentJourney, hasMonthlyPrices, changeBudgetType]
    );

    return (
        <div className={`${className} ${mobileHide ? '' : 'filters-opened'}`}>
            <div className="filterbox-body">
                <div className="budgetWrapper">
                    <button
                        className={`toggle-filters  ${mobileHide ? 'toggled' : ''}`}
                        onClick={() => setMobileHide(!mobileHide)}
                    >
                        <span>
                            <Icon name={Icons.Settings} width={32} height={32} color={theme.colors.white} />
                            {t('filters.filters')}

                            {selectedFiltersCount > 0 && (
                                <span className="filters-title-badge">{selectedFiltersCount}</span>
                            )}
                        </span>
                        <Icon
                            name={mobileHide ? Icons.Plus : Icons.Minus}
                            width={20}
                            height={20}
                            color={theme.colors.white}
                        />
                    </button>

                    <h3 className="filter-title show-if-desktop">{t('filters.category.paymentJourneyType')}</h3>

                    <label className="geolocation-filter-label">{t('GeoLocationInput.label')}</label>
                </div>

                <GeneratedFilterButtons
                    availableFilters={availableFilters}
                    filterCategories={filterCategories}
                    handleChange={handleChange}
                    mobileHide={mobileHide}
                    priceRange={priceRange}
                    disabled={isRouting}
                    filters={baseFilters}
                    onGeoLocationChange={onGeoLocationChange}
                    renderFinanceToggle={renderFinanceToggle}
                />
            </div>

            {!isHomepage && (
                <div className={`filterbox-extra-filters ${!areExtraFiltersCollapsed || !mobileHide ? 'visible' : ''}`}>
                    <GeneratedExtraFilters
                        handleChange={handleChange}
                        extraFilters={extraFilters}
                        availableFilters={availableFilters}
                        filterCategories={filterCategories}
                    />
                </div>
            )}

            <PriceLegalText className={`frontpage-legaltext-over ${mobileHide ? 'disabled' : ''}`} isStockJourney>
                <p className="frontpage-legaltext">
                    <span dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('filters.stock.legal.text')}></span>
                </p>
            </PriceLegalText>

            {renderResetButton(`show-if-desktop ${paymentJourney === FINANCE ? 'filters-reset-finance-journey' : ''}`)}

            {!isHomepage && (
                <button
                    className={`filterbox-collapse-button ${areExtraFiltersCollapsed ? 'collapsed' : ''}`}
                    onClick={handleCollapseExtraFiltersClick}
                >
                    {areExtraFiltersCollapsed ? t('filters.showMore') : t('filters.hideMore')}
                    <Icon height={8} width={14} name={Icons.ChevronDown} />
                </button>
            )}

            {renderResetButton(
                `show-if-mobile ${paymentJourney === FINANCE ? 'filters-reset-finance-journey' : ''} ${
                    mobileHide ? 'hidden' : ''
                }`
            )}

            <button
                className={`show-if-mobile mobile-filter-close-button ${mobileHide ? 'hidden' : ''}`}
                onClick={() => setMobileHide(true)}
            >
                {t('filters.hideMore')}
                <Icon height={8} width={14} name={Icons.ChevronDown} />
            </button>
        </div>
    );
};

export default FilterBoxTemplate;
